import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function AboutPage() {
  const [contactForm, setContactForm] = useState({
    submitting: false,
    success: false,
    error: '',
  });

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="phone_alt"]') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setContactForm({ submitting: false, success: true, error: '' });
      return;
    }

    setContactForm({ submitting: true, success: false, error: '' });

    try {
      const formData = new FormData(form);
      const submitAddr = 'https://readdy.ai/api/form/d9niuse8mbnljin5a1ig';
      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setContactForm({ submitting: false, success: true, error: '' });
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后再试';
        setContactForm({ submitting: false, success: false, error: msg });
      }
    } catch {
      setContactForm({ submitting: false, success: false, error: '网络错误，请检查网络后重试' });
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-12">
        <div className="mx-auto max-w-[1440px] px-4 md:px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm mb-6" aria-label="面包屑导航">
            <Link to="/" className="text-foreground-400 hover:text-foreground-200 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-foreground-500" />
            <span className="text-foreground-200 font-medium">关于我们</span>
          </nav>

          {/* Page Header */}
          <div className="text-center mb-12 md:mb-16">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-100 mb-4">关于陌陌影视</h1>
            <p className="text-sm md:text-base text-foreground-400 max-w-2xl mx-auto leading-relaxed">
              成立于2018年，陌陌影视致力于为全球华语用户提供高品质的免费在线观影服务。五年来，我们始终坚持"高清、免费、流畅"的理念，为数千万用户带来了极致的观影体验。
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
            {[
              { value: '5000万+', label: '累计用户', icon: 'ri-user-line' },
              { value: '10万+', label: '电影资源', icon: 'ri-movie-2-line' },
              { value: '99.9%', label: '服务可用率', icon: 'ri-shield-check-line' },
              { value: '4.9/5', label: '用户评分', icon: 'ri-star-line' },
            ].map((stat) => (
              <div key={stat.label} className="rounded-xl bg-background-100/60 p-5 md:p-6 text-center">
                <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-primary-500/10 flex items-center justify-center">
                  <i className={`${stat.icon} text-primary-400 text-lg`} />
                </div>
                <p className="font-heading text-2xl font-bold text-primary-400 mb-1">{stat.value}</p>
                <p className="text-xs text-foreground-400">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Brand Story */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-14">
            <div className="rounded-xl overflow-hidden h-[300px] md:h-[400px]">
              <img
                src="https://readdy.ai/api/search-image?query=modern%20minimalist%20office%20with%20movie%20posters%20on%20wall%20and%20people%20watching%20film%20on%20large%20screen%20warm%20cozy%20lighting%20cinematic%20atmosphere%20creative%20workspace&width=800&height=500&seq=about-brand-story&orientation=landscape"
                alt="陌陌影视品牌故事"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex flex-col justify-center">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-100 mb-4">品牌故事</h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-300 leading-relaxed">
                <p>陌陌影视的创始团队是一群热爱电影的年轻人。2018年，因为发现很多优质电影缺乏便捷的观看渠道，他们决定创建一个真正以用户为中心的免费观影平台。</p>
                <p>从最初只有一个服务器的小网站，到如今服务数千万用户的专业平台，陌陌影视始终坚持做一件事：<strong>让每个人都能轻松看到好电影</strong>。</p>
                <p>我们不仅提供最新最热的院线大片，更珍视那些被时光沉淀的经典作品。在陌陌影视，你可以找到从默片时代到数字时代的电影瑰宝，感受电影艺术跨越百年的魅力。</p>
              </div>
            </div>
          </section>

          {/* Reputation */}
          <section className="mb-14">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-100 text-center mb-8">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { name: '张先生', role: '资深影迷', text: '用了陌陌影视三年了，片库真的很全，从好莱坞大片到小众文艺片都有。画质清晰流畅，推荐给了很多朋友！', rating: 5 },
                { name: '李女士', role: '全职妈妈', text: '周末带娃一起看电影成了我们的家庭传统。陌陌影视的动画片分类很丰富，孩子特别喜欢，而且完全免费，太良心了！', rating: 5 },
                { name: '王同学', role: '大学生', text: '宿舍里大家都用陌陌影视看电影，更新速度真的快，新片上线基本不用等。而且不需要注册会员，打开就能看，超级方便。', rating: 5 },
              ].map((review) => (
                <div key={review.name} className="rounded-xl bg-background-100/60 p-6">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm" />
                    ))}
                  </div>
                  <p className="text-sm text-foreground-200 leading-relaxed mb-4">&ldquo;{review.text}&rdquo;</p>
                  <div>
                    <p className="text-sm font-semibold text-foreground-100">{review.name}</p>
                    <p className="text-xs text-foreground-400">{review.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-100 mb-4">联系我们</h2>
              <p className="text-sm text-foreground-400 mb-6 leading-relaxed">
                无论您有任何问题、建议还是合作意向，都欢迎随时联系我们。我们的客服团队会在24小时内回复您的消息。
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-400 text-lg" />
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">客服邮箱</p>
                    <p className="text-sm text-foreground-200">service@momomovies.com</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-customer-service-line text-primary-400 text-lg" />
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">客服热线</p>
                    <p className="text-sm text-foreground-200">400-888-6666</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-primary-400 text-lg" />
                  </div>
                  <div>
                    <p className="text-xs text-foreground-400">工作时间</p>
                    <p className="text-sm text-foreground-200">周一至周日 9:00 - 21:00</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-xl bg-background-100/60 p-6">
              <h3 className="font-heading text-lg font-semibold text-foreground-100 mb-5">发送消息</h3>
              <form data-readdy-form="" id="contact-form-about" onSubmit={handleContactSubmit} className="space-y-4">
                <input type="text" name="phone_alt" className="hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                <div>
                  <label htmlFor="contact-name" className="block text-sm text-foreground-300 mb-1.5">姓名</label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-200 border border-background-300/40 text-sm text-foreground-100 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/50 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm text-foreground-300 mb-1.5">邮箱</label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-200 border border-background-300/40 text-sm text-foreground-100 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/50 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm text-foreground-300 mb-1.5">留言</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 rounded-lg bg-background-200 border border-background-300/40 text-sm text-foreground-100 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  />
                </div>
                <button
                  type="submit"
                  disabled={contactForm.submitting}
                  className="w-full py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
                >
                  {contactForm.submitting ? '提交中...' : '发送消息'}
                </button>
                {contactForm.success && (
                  <p className="text-sm text-accent-400 text-center">消息已发送成功，我们会尽快回复您！</p>
                )}
                {contactForm.error && (
                  <p className="text-sm text-red-400 text-center">{contactForm.error}</p>
                )}
              </form>
            </div>
          </section>
        </div>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于陌陌影视',
            description: '陌陌影视成立于2018年，致力于为全球华语用户提供高品质的免费在线观影服务。',
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.momomovies.com/' },
                { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.momomovies.com/about' },
              ],
            },
          }),
        }}
      />
    </>
  );
}